import { useEffect, useState } from "react";

import {
  chapterRangeFor,
  chapterSpans,
  describeChapters,
  dragChapters,
  spanIdForRange,
  toggleChapter,
  type ChapterRange,
} from "@/data/chapter-range";
import { WHOLE_BOOK_SPAN_ID, type TimelineSpan } from "@/data/join-chapters";
import { useFoldAway } from "./useFoldAway";

interface TimelineChapterPickerProps {
  spans: readonly TimelineSpan[];
  spanId: string;
  onSpanIdChange: (id: string) => void;
}

function hintFor(current: ChapterRange, index: number): string {
  const next = toggleChapter(current, index);
  return next.first === current.first && next.last === current.last
    ? "At least one chapter has to play"
    : `Click to play ${describeChapters(next)}`;
}

/** Past this, a row of buttons takes over the screen, so they fold away behind one. */
const FOLD_OVER_CHAPTERS = 12;

interface ChapterDrag {
  start: number;
  end: number;
}

/** The chapter button under a finger or pointer, wherever the press began. */
function chapterAt(event: PointerEvent): number | null {
  const button = document.elementFromPoint(event.clientX, event.clientY)
    ?.closest<HTMLElement>("[data-chapter-index]");
  return button?.dataset.chapterIndex === undefined ? null : Number(button.dataset.chapterIndex);
}

/** A run of chapters to play: lit ones play, and a click can never leave a gap. */
export function TimelineChapterPicker({
  spans,
  spanId,
  onSpanIdChange,
}: TimelineChapterPickerProps) {
  const [hint, setHint] = useState<string | null>(null);
  // Joining chapters can hold the page for a second, so the buttons answer first.
  const [pendingId, setPendingId] = useState<string | null>(null);
  useEffect(() => {
    if (pendingId === null || pendingId === spanId) {
      setPendingId(null);
      return;
    }
    const frame = requestAnimationFrame(() => {
      setTimeout(() => onSpanIdChange(pendingId), 0);
    });
    return () => cancelAnimationFrame(frame);
  }, [onSpanIdChange, pendingId, spanId]);
  const [drag, setDrag] = useState<ChapterDrag | null>(null);
  const { rootRef, unfolded, toggle } = useFoldAway();
  const shownId = pendingId ?? spanId;
  const chapters = chapterSpans(spans);
  const committed = chapterRangeFor(spans, shownId);
  const range = drag === null || committed === null
    ? committed
    : dragChapters(committed, drag.start, drag.end);
  useEffect(() => {
    if (drag === null) {
      return;
    }
    const onMove = (event: PointerEvent): void => {
      const end = chapterAt(event);
      if (end !== null) {
        setDrag((current) => (current === null ? null : { ...current, end }));
      }
    };
    const onUp = (): void => {
      if (committed !== null) {
        setPendingId(spanIdForRange(spans, dragChapters(committed, drag.start, drag.end)));
      }
      setDrag(null);
    };
    const onCancel = (): void => setDrag(null);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
    };
  }, [committed, drag, spans]);
  if (chapters.length < 2 || range === null) {
    return null;
  }
  const allPlaying = shownId === WHOLE_BOOK_SPAN_ID;
  const allHint = allPlaying ? "Playing all of it" : "Click to play all of it";
  const showHint = (text: string) => ({
    onMouseEnter: () => setHint(text),
    onMouseLeave: () => setHint(null),
    onFocus: () => setHint(text),
    onBlur: () => setHint(null),
    title: text,
  });
  const folds = chapters.length > FOLD_OVER_CHAPTERS;
  const status = pendingId !== null
    ? "Loading chapters…"
    : hint ?? `Playing ${allPlaying ? "all chapters" : describeChapters(range)}`;
  return (
    <div
      ref={rootRef}
      className="timeline-chapters"
      data-folds={folds}
      data-unfolded={unfolded}
    >
      {folds ? (
        <button
          type="button"
          className="timeline-chapters__fold"
          aria-expanded={unfolded}
          onClick={toggle}
        >
          <span className="timeline-chapters__hint" aria-live="polite">{status}</span>
          <span aria-hidden="true">{unfolded ? "▴" : "▾"}</span>
        </button>
      ) : (
        <p className="timeline-chapters__hint" aria-live="polite">{status}</p>
      )}
      <div className="timeline-chapters__row" role="group" aria-label="Chapters to play">
        <button
          type="button"
          className="timeline-chapters__all"
          aria-pressed={allPlaying}
          {...showHint(allHint)}
          onClick={() => setPendingId(WHOLE_BOOK_SPAN_ID)}
        >
          All
        </button>
        {chapters.map((chapter, index) => (
          <button
            key={chapter.id}
            type="button"
            aria-pressed={index >= range.first && index <= range.last}
            aria-label={chapter.label}
            {...showHint(hintFor(range, index))}
            data-chapter-index={index}
            onPointerDown={(event) => {
              if (event.button === 0) {
                setDrag({ start: index, end: index });
              }
            }}
            onClick={(event) => {
              // A pointer press is handled on release. Detail 0 is the keyboard.
              if (event.detail === 0) {
                setPendingId(spanIdForRange(spans, toggleChapter(range, index)));
              }
            }}
          >
            {index + 1}
          </button>
        ))}
      </div>
    </div>
  );
}
